/**
 * Types and constants of the FieldCheck domain (templates, inspections,
 * findings). Single source for UI + logic — values go EXACTLY to the
 * DB (text columns), so they are not renamed lightly.
 */

export const TEMPLATE_ITEM_TYPES = ["yes_no", "number", "text", "photo"] as const;
export type TemplateItemType = (typeof TEMPLATE_ITEM_TYPES)[number];

export const INSPECTION_STATUSES = ["draft", "in_progress", "completed"] as const;
export type InspectionStatus = (typeof INSPECTION_STATUSES)[number];

export const FINDING_SEVERITIES = ["low", "medium", "high", "critical"] as const;
export type FindingSeverity = (typeof FINDING_SEVERITIES)[number];

/**
 * Finding state flow: open → in_progress → resolved.
 *
 * The legacy terminal value 'closed' (previous binary version) is normalized to
 * 'resolved' (see `normalizeFindingStatus`). The array order is the pipeline
 * order (used for UI filters).
 */
export const FINDING_STATUSES = ["open", "in_progress", "resolved"] as const;
export type FindingStatus = (typeof FINDING_STATUSES)[number];

/** Terminal state (closed): the finding has already been resolved. */
export const FINDING_TERMINAL_STATUS: FindingStatus = "resolved";

export const ANSWER_RESULTS = ["pass", "fail"] as const;
export type AnswerResult = (typeof ANSWER_RESULTS)[number];

export function isTemplateItemType(value: string | null | undefined): value is TemplateItemType {
  return TEMPLATE_ITEM_TYPES.some((type) => type === value);
}

export function isInspectionStatus(value: string | null | undefined): value is InspectionStatus {
  return INSPECTION_STATUSES.some((status) => status === value);
}

export function isFindingSeverity(value: string | null | undefined): value is FindingSeverity {
  return FINDING_SEVERITIES.some((severity) => severity === value);
}

export function isFindingStatus(value: string | null | undefined): value is FindingStatus {
  return FINDING_STATUSES.some((status) => status === value);
}

/**
 * Normalizes the raw DB state to one in the current pipeline:
 *  - 'closed' (legacy) → 'resolved'
 *  - any unknown/null value → 'open'
 */
export function normalizeFindingStatus(value: string | null | undefined): FindingStatus {
  if (value === "closed") {
    return "resolved";
  }
  return isFindingStatus(value) ? value : "open";
}

/** Is the finding resolved (terminal state)? Tolerates the legacy 'closed'. */
export function isFindingResolved(value: string | null | undefined): boolean {
  return normalizeFindingStatus(value) === FINDING_TERMINAL_STATUS;
}
